import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, ErrorApi } from '../api/cliente.js';
import { ImportarEmpleados } from './ImportarEmpleados.js';

interface TrabajadorPortal {
  id: string;
  dni: string;
  nombre: string;
  legajo: string | null;
  email: string | null;
  telefono: string | null;
  domicilio: string | null;
  sexo: string | null;
  edad: number | null;
  fecha_nacimiento: string | null;
  activo: number;
  baja_en: string | null;
}

const FORM_INICIAL = {
  dni: '',
  nombre: '',
  legajo: '',
  email: '',
  telefono: '',
  domicilio: '',
  sexo: '',
  fechaNacimiento: '',
};

export function Empleados() {
  const [trabajadores, setTrabajadores] = useState<TrabajadorPortal[] | null>(null);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [modalImportarAbierto, setModalImportarAbierto] = useState(false);
  const [editando, setEditando] = useState<TrabajadorPortal | null>(null);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [errorEdicion, setErrorEdicion] = useState('');
  const [formEdicion, setFormEdicion] = useState(FORM_INICIAL);

  async function cargarTrabajadores() {
    try {
      const res = await api.get<TrabajadorPortal[]>('/api/portal/trabajadores?incluirInactivos=1');
      setTrabajadores(res);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar los empleados');
    }
  }

  async function cambiarEstado(t: TrabajadorPortal) {
    const activar = t.activo !== 1;
    const accion = activar ? 'reactivar' : 'dar de baja';
    const confirmado = window.confirm(
      activar
        ? `Vas a reactivar a ${t.nombre}. Podra volver a usarse en nuevas solicitudes de control.`
        : `Vas a dar de baja a ${t.nombre}. No se borrara su historial, pero no aparecera para nuevas solicitudes de control.`,
    );
    if (!confirmado) return;

    setError('');
    try {
      await api.post(`/api/portal/trabajadores/${t.id}/estado`, { activo: activar });
      await cargarTrabajadores();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : `No se pudo ${accion} al empleado`);
    }
  }

  useEffect(() => {
    void cargarTrabajadores();
  }, []);

  const trabajadoresFiltrados = useMemo(() => {
    if (!trabajadores) return [];
    const q = busqueda.toLowerCase().trim();
    if (!q) return trabajadores;
    return trabajadores.filter(
      (t) =>
        t.nombre.toLowerCase().includes(q) ||
        t.dni.includes(q) ||
        (t.legajo && t.legajo.toLowerCase().includes(q)),
    );
  }, [trabajadores, busqueda]);

  function abrirEditar(t: TrabajadorPortal) {
    setEditando(t);
    setErrorEdicion('');
    setFormEdicion({
      dni: t.dni,
      nombre: t.nombre,
      legajo: t.legajo ?? '',
      email: t.email ?? '',
      telefono: t.telefono ?? '',
      domicilio: t.domicilio ?? '',
      sexo: t.sexo ?? '',
      fechaNacimiento: t.fecha_nacimiento ?? '',
    });
  }

  async function guardarEdicion(e: React.FormEvent) {
    e.preventDefault();
    if (!editando) return;
    setGuardandoEdicion(true);
    setErrorEdicion('');
    try {
      await api.put(`/api/portal/trabajadores/${editando.id}`, formEdicion);
      await cargarTrabajadores();
      setEditando(null);
    } catch (err) {
      setErrorEdicion(err instanceof ErrorApi ? err.message : 'No se pudo modificar el empleado');
    } finally {
      setGuardandoEdicion(false);
    }
  }

  return (
    <>
      <div className="fila" style={{ marginBottom: 20, alignItems: 'center' }}>
        <div>
          <h2>Nomina de Empleados</h2>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Listado completo del personal registrado para controles de ausentismo medico.
          </p>
        </div>
        <button className="secundario" style={{ width: 'auto' }} onClick={() => setModalImportarAbierto(true)}>
          Importar Empleados
        </button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Buscar por nombre, DNI o legajo..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      {error && <div className="error-box">{error}</div>}
      {trabajadores === null && <div className="cargando">Cargando nomina de empleados...</div>}
      {trabajadores && trabajadores.length === 0 && (
        <div className="tarjeta glass" style={{ padding: 24, textAlign: 'center' }}>
          <p className="muted" style={{ margin: '0 0 16px' }}>No hay empleados registrados en el sistema.</p>
          <button className="primario" style={{ width: 'auto' }} onClick={() => setModalImportarAbierto(true)}>
            Importar primer lote de empleados
          </button>
        </div>
      )}

      {trabajadores && trabajadores.length > 0 && (
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Nombre completo</th>
                <th>Legajo</th>
                <th>Sexo</th>
                <th>Edad / F. nac.</th>
                <th>Contacto y domicilio</th>
                <th>Estado</th>
                <th style={{ textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {trabajadoresFiltrados.map((t) => (
                <tr key={t.id} className={t.activo === 1 ? undefined : 'fila-inactiva'}>
                  <td><strong>{t.dni}</strong></td>
                  <td>{t.nombre}</td>
                  <td>{t.legajo ? <span className="insignia gris">{t.legajo}</span> : <span className="muted">-</span>}</td>
                  <td>
                    {t.sexo ? (
                      <span className={`insignia ${t.sexo === 'Hombre' ? 'azul' : t.sexo === 'Mujer' ? 'ambar' : 'gris'}`}>
                        {t.sexo}
                      </span>
                    ) : (
                      <span className="muted">-</span>
                    )}
                  </td>
                  <td>
                    {t.edad !== null ? (
                      <span>
                        {t.edad} anios
                        {t.fecha_nacimiento && <div className="mini muted">{t.fecha_nacimiento}</div>}
                      </span>
                    ) : (
                      <span className="muted">-</span>
                    )}
                  </td>
                  <td>
                    {t.domicilio && <div>{t.domicilio}</div>}
                    {t.email && <div className="mini muted" style={{ marginTop: 2 }}>{t.email}</div>}
                    {t.telefono && <div className="mini muted" style={{ marginTop: 2 }}>Tel. {t.telefono}</div>}
                    {!t.domicilio && !t.email && !t.telefono && <span className="muted">-</span>}
                  </td>
                  <td>
                    {t.activo === 1 ? (
                      <span className="insignia verde">Activo</span>
                    ) : (
                      <>
                        <span className="insignia roja">Baja</span>
                        {t.baja_en && <div className="mini muted" style={{ marginTop: 4 }}>Desde {t.baja_en.slice(0, 10)}</div>}
                      </>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div className="acciones-tabla">
                      <button className="secundario" style={{ width: 'auto' }} onClick={() => abrirEditar(t)}>
                        Editar
                      </button>
                      <button
                        className={t.activo === 1 ? 'peligro' : 'secundario'}
                        style={{ width: 'auto' }}
                        onClick={() => void cambiarEstado(t)}
                      >
                        {t.activo === 1 ? 'Dar de baja' : 'Reactivar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalImportarAbierto &&
        createPortal(
          <ImportarEmpleados
            onCerrar={() => setModalImportarAbierto(false)}
            onExito={() => {
              setModalImportarAbierto(false);
              void cargarTrabajadores();
            }}
          />,
          document.body,
        )}

      {editando &&
        createPortal(
          <div className="capa-bloqueo" onClick={(e) => { if (e.target === e.currentTarget) setEditando(null); }}>
            <div className="dialogo glass">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--borde)', paddingBottom: 12, marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 18 }}>Modificar empleado</h3>
                <button type="button" className="fantasma" onClick={() => setEditando(null)} aria-label="Cerrar">
                  X
                </button>
              </div>

              {errorEdicion && <div className="error-box">{errorEdicion}</div>}

              <form onSubmit={guardarEdicion}>
                <div className="portal-form-grid">
                  <div className="campo">
                    <label htmlFor="edit-dni">DNI</label>
                    <input
                      id="edit-dni"
                      value={formEdicion.dni}
                      onChange={(e) => setFormEdicion((f) => ({ ...f, dni: e.target.value.replace(/\D/g, '') }))}
                      required
                    />
                  </div>
                  <div className="campo">
                    <label htmlFor="edit-nombre">Nombre completo</label>
                    <input
                      id="edit-nombre"
                      value={formEdicion.nombre}
                      onChange={(e) => setFormEdicion((f) => ({ ...f, nombre: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="campo">
                    <label htmlFor="edit-legajo">Legajo</label>
                    <input
                      id="edit-legajo"
                      value={formEdicion.legajo}
                      onChange={(e) => setFormEdicion((f) => ({ ...f, legajo: e.target.value }))}
                    />
                  </div>
                  <div className="campo">
                    <label htmlFor="edit-sexo">Sexo</label>
                    <select
                      id="edit-sexo"
                      value={formEdicion.sexo}
                      onChange={(e) => setFormEdicion((f) => ({ ...f, sexo: e.target.value }))}
                    >
                      <option value="">Sin especificar</option>
                      <option value="Hombre">Hombre</option>
                      <option value="Mujer">Mujer</option>
                    </select>
                  </div>
                  <div className="campo">
                    <label htmlFor="edit-fecha">Fecha de nacimiento</label>
                    <input
                      id="edit-fecha"
                      type="date"
                      value={formEdicion.fechaNacimiento}
                      onChange={(e) => setFormEdicion((f) => ({ ...f, fechaNacimiento: e.target.value }))}
                    />
                  </div>
                  <div className="campo">
                    <label htmlFor="edit-email">Email</label>
                    <input
                      id="edit-email"
                      type="email"
                      value={formEdicion.email}
                      onChange={(e) => setFormEdicion((f) => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                  <div className="campo">
                    <label htmlFor="edit-telefono">Telefono</label>
                    <input
                      id="edit-telefono"
                      value={formEdicion.telefono}
                      onChange={(e) => setFormEdicion((f) => ({ ...f, telefono: e.target.value }))}
                    />
                  </div>
                  <div className="campo campo-ancho">
                    <label htmlFor="edit-domicilio">Domicilio</label>
                    <input
                      id="edit-domicilio"
                      value={formEdicion.domicilio}
                      onChange={(e) => setFormEdicion((f) => ({ ...f, domicilio: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="fila" style={{ gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button type="button" className="secundario" onClick={() => setEditando(null)} disabled={guardandoEdicion}>
                    Cancelar
                  </button>
                  <button type="submit" className="primario" disabled={guardandoEdicion}>
                    {guardandoEdicion ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
